import React, { useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import AvatarRenderer from '../components/AvatarRenderer';
import AvatarSavePanel from '../components/AvatarSavePanel';
import MyCreations from '../components/MyCreations';

import AvatarPostSelector from '../components/AvatarPostSelector';

import {
  createStudioVisit,
  createStudioAnalyticsEvent,
  trackStudioEvent,
} from '../services/studioAnalyticsApi';

import {
  avatarCategories,
  avatarOptions,
  getAvatarOption,
} from '../data/avatarOptions';

import {
  createDefaultAvatar,
  normalizeAvatarConfig,
} from '../data/avatarDefaults';

import '../styles/AvatarCreator.css';

function CategoryIcon({ category }) {
  const artwork = {
    characterType: (
      <>
        <circle cx="12" cy="7" r="4" />
        <path d="M5 22v-3a7 7 0 0 1 14 0v3" />
      </>
    ),

    head: (
      <path d="M7 4h10a4 4 0 0 1 4 4v7a9 9 0 0 1-18 0V8a4 4 0 0 1 4-4Z" />
    ),

    skinTone: (
      <>
        <path d="M12 2S4 11 4 16a8 8 0 0 0 16 0c0-5-8-14-8-14Z" />
        <path d="M8 16a4 4 0 0 0 4 4" />
      </>
    ),

    hair: (
      <>
        <path d="m4 9 5-5a3 3 0 0 1 4 0l8 8a3 3 0 0 1 0 4l-5 5" />
        <path d="m6 7 12 12M9 10l-5 5m8-2-5 5m8-2-5 5" />
      </>
    ),


    eyeShape: (
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),

    expression: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M8 9v2m8-2v2M8 15q4 5 8 0" />
      </>
    ),

    clothing: (
      <path d="m8 4-6 4 3 5 3-2v11h8V11l3 2 3-5-6-4q-4 5-8 0Z" />
    ),

    background: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <circle cx="9" cy="9" r="2" />
        <path d="m4 19 6-6 4 4 3-3 4 4" />
      </>
    ),

    creations: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </>
    ),
  };

  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {artwork[category] || artwork.characterType}
    </svg>
  );
}

// Show the relevant part of the character in each option.

const THUMBNAIL_VIEWS = {
  characterType: '65 35 270 365',
  head: '85 75 230 220',
  eyeShape: '125 140 150 90',
  expression: '85 75 230 220',
  hair: '65 30 270 300',
  clothing: '70 275 260 125',
};

export default function AvatarCreator({ onProfileUpdated }) {
  const navigate = useNavigate();

  const headingRef = useRef(null);
  const choicesRef = useRef(null);
  const categoryRefs = useRef({});

  const [avatar, setAvatar] = useState(createDefaultAvatar);
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [editingCreation, setEditingCreation] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreations, setShowCreations] = useState(false);
  const [savedCreation, setSavedCreation] = useState(null);


  const [showPostSelector, setShowPostSelector] = useState(false);

  const location = useLocation();

  // One visit for this mounted Studio screen.
  // Moving between editor, collection and selector keeps this visit.
  const [studioVisit] = useState(() =>
    createStudioVisit(location.state?.studioEntryPoint)
  );

  const [studioOpenEvent] = useState(() =>
    createStudioAnalyticsEvent('studio_opened', studioVisit)
  );

  const openTrackedRef = useRef(false);
  const customizationTrackedRef = useRef(false);

  // Keep option comparisons current, including rapid taps.
  const avatarRef = useRef(avatar);

  useEffect(() => {
    if (openTrackedRef.current) return;

    openTrackedRef.current = true;
    void trackStudioEvent(studioOpenEvent);
  }, [studioOpenEvent]);

  const category = avatarCategories[categoryIndex];
  const options = avatarOptions[category.id];

  const isColor =
    category.id === 'skinTone' || category.id === 'background';


  const paletteCategory =
    category.id === 'eyeShape'
      ? 'eyeColor'
      : category.id === 'hair'
        ? 'hairColor'
        : category.id === 'clothing'
          ? 'clothingColor'
          : null;


  const paletteOptions = paletteCategory
    ? avatarOptions[paletteCategory]
    : [];

  const background = getAvatarOption(
    'background',
    avatar.background
  );

  // Prevent the page behind the creator from scrolling.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Reset option scrolling and reveal the active category.
  useEffect(() => {
    if (showPostSelector) return;

    if (choicesRef.current) {
      choicesRef.current.scrollTop = 0;
    }

    const activeId = showCreations ? 'creations' : category.id;
    const button = categoryRefs.current[activeId];
    const strip = button?.parentElement;

    if (!button || !strip) return;

    const left = button.offsetLeft;
    const right = left + button.offsetWidth;

    if (left < strip.scrollLeft) {
      strip.scrollLeft = left;
    } else if (right > strip.scrollLeft + strip.clientWidth) {
      strip.scrollLeft = right - strip.clientWidth;
    }
  }, [category.id, finished, showCreations, showPostSelector]);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [finished, showCreations, showPostSelector]);



  function selectOption(
    optionId,
    targetCategory = category.id
  ) {
    const current = avatarRef.current;

    // Tapping the selected option is not experimentation.
    if (current[targetCategory] === optionId) return;

    const nextAvatar = {
      ...current,
      [targetCategory]: optionId,
    };

    avatarRef.current = nextAvatar;
    setAvatar(nextAvatar);

    if (!customizationTrackedRef.current) {
      customizationTrackedRef.current = true;

      void trackStudioEvent(
        createStudioAnalyticsEvent(
          'avatar_customization_started',
          studioVisit
        )
      );
    }
  }



  function handleSaved(creation) {
    setEditingCreation(creation);
    setSavedCreation(creation);
    setFinished(false);
    setShowPostSelector(false);
    setShowCreations(true);
  }


  function handleEditCreation(creation) {

    const nextAvatar = normalizeAvatarConfig(creation.config);
    avatarRef.current = nextAvatar;

    setEditingCreation(creation);
    setAvatar(nextAvatar);
    setSavedCreation(null);
    setFinished(false);
    setShowPostSelector(false);
    setShowCreations(false);

    void trackStudioEvent(
      createStudioAnalyticsEvent('avatar_edit_opened', {
        ...studioVisit,
        creationId: creation._id,
      })
    );
  }


  function handleNewCreation() {
    const nextAvatar = createDefaultAvatar();

    avatarRef.current = nextAvatar;

    setAvatar(nextAvatar);
    setEditingCreation(null);
    setSavedCreation(null);
    setCategoryIndex(0);
    setFinished(false);
    setShowPostSelector(false);
    setShowCreations(false);

    void trackStudioEvent(
      createStudioAnalyticsEvent(
        'avatar_new_started',
        studioVisit
      )
    );
  }


  function handleDeletedCreation(id) {
    if (editingCreation?._id === id) {
      setEditingCreation(null);
    }

    setSavedCreation((current) =>
      current?._id === id ? null : current
    );
  }

  function handleOpenPostSelector() {
    if (isSaving) return;

    setShowPostSelector(true);
  }

  function handleShareAvatarsAsPost(creations) {
    if (!Array.isArray(creations) || creations.length === 0) {
      throw new Error('Select at least one avatar to share.');
    }

    const requestId =
      window.crypto?.randomUUID?.() ||
      `avatar_share_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2)}`;


    // Records leaving the selector for the composer.
    // Successful publication is recorded separately on the backend.
    const handoffEvent = createStudioAnalyticsEvent(
      'avatar_post_composer_opened',
      {
        ...studioVisit,
        studioImageCount: creations.length,
      }
    );

    void trackStudioEvent(handoffEvent);

    navigate('/create-post', {
      state: {
        avatarShare: {
          requestId,


          creations: creations.map((creation) => ({
            _id: creation._id,
            name: creation.name,
            imageUrl: creation.imageUrl,
          })),
        },
      },
    });
  }



function handleBack() {
  if (isSaving) return;

  if (showPostSelector) {
    setShowPostSelector(false);
    return;
  }

  if (finished) {
    setFinished(false);
    return;
  }

  if (showCreations) {
    setShowCreations(false);
    return;
  }

  // Return to the screen that opened Studio.
  if (location.state?.from === 'home') {
    navigate('/home', {
      replace: true,
      state: {
        restoreHomeScroll: true,
      },
    });
    return;
  }

  // Normal navigation from another screen.
  // React Router's index avoids leaving the app on direct visits.
  if (Number(window.history.state?.idx) > 0) {
    navigate(-1);
    return;
  }

  // Fallback when Studio was opened directly.
  navigate('/edit-profile', { replace: true });
}




  function handleHeaderAction() {
    if (isSaving || showPostSelector) return;

    if (showCreations) {
      handleNewCreation();
      return;
    }

    setFinished((current) => !current);
  }

  return (
    <main
      className={`avatar-creator ${
        finished ? 'is-finished' : ''
      }`}
    >
      <header className="avatar-creator__header">
        <button
          type="button"
          className="avatar-creator__back"
          onClick={handleBack}
          disabled={isSaving}
          aria-label={
            showPostSelector
              ? 'Back to My Creations'
              : finished || showCreations
                ? 'Return to editing'
                : 'Go Back'
          }
        >
          <svg
            width="25"
            height="25"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m14 5-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <h1 ref={headingRef} tabIndex={-1}>
          {showPostSelector
            ? 'Select avatars'
            : showCreations
              ? 'My Creations'
              : finished
                ? 'Avatar Preview'
                : 'Edit Avatar'}
        </h1>

        {!showPostSelector && (
          <button
            type="button"
            className="avatar-creator__done"
            disabled={isSaving}
            onClick={handleHeaderAction}
          >
            {showCreations ? 'New' : finished ? 'Edit' : 'Done'}
          </button>
        )}
      </header>

      {!showCreations && (
        <section
          className="avatar-creator__stage"
          style={{ backgroundColor: background.color }}
          aria-label="Live avatar preview"
        >
          <AvatarRenderer
            avatar={avatar}
            size={400}
            viewBox={
              finished ? '0 0 400 400' : '0 25 400 375'
            }
            showBackground={finished}
            rounded={false}
            className="avatar-creator__portrait"
            label="Your customized BarterFi avatar"
          />
        </section>
      )}

      {finished ? (
        <AvatarSavePanel
          avatar={avatar}
          creation={editingCreation}
          onSaved={handleSaved}
          onBusyChange={setIsSaving}
        />
      ) : (
        <>
          <nav
            className="avatar-creator__categories"
            aria-label="Customization categories"
            style={
              showPostSelector ? { display: 'none' } : undefined
            }
          >
            {avatarCategories.map((item, index) => {
              const active =
                !showCreations && item.id === category.id;

              return (
                <button
                  key={item.id}
                  ref={(element) => {
                    categoryRefs.current[item.id] = element;
                  }}
                  type="button"
                  className={`avatar-creator__category ${
                    active ? 'is-active' : ''
                  }`}
                  aria-label={item.label}
                  aria-pressed={active}
                  title={item.label}
                  onClick={() => {
                    setShowCreations(false);
                    setCategoryIndex(index);
                  }}
                >
                  <CategoryIcon category={item.id} />
                </button>
              );
            })}

            <button
              ref={(element) => {
                categoryRefs.current.creations = element;
              }}
              type="button"
              className={`avatar-creator__category avatar-creator__category--collection ${
                showCreations ? 'is-active' : ''
              }`}
              aria-label="My Creations"
              aria-pressed={showCreations}
              title="My Creations"
              onClick={() => setShowCreations(true)}
            >
              <CategoryIcon category="creations" />
            </button>
          </nav>

          {showPostSelector && (
            <AvatarPostSelector
              onShareAsPost={handleShareAvatarsAsPost}
            />
          )}

          {showCreations ? (
            <div
              style={{
                display: showPostSelector ? 'none' : 'flex',
                flexDirection: 'column',
                flex: '1 1 0',
                minHeight: 0,
              }}
              aria-hidden={showPostSelector ? true : undefined}
            >
              <MyCreations
                savedCreation={savedCreation}
                onEdit={handleEditCreation}
                onDeleted={handleDeletedCreation}
                onProfileUpdated={onProfileUpdated}
                onShareAsPost={handleOpenPostSelector}
              />
            </div>
          ) : (
            <section
              ref={choicesRef}
              className="avatar-creator__choices"
              aria-label={`${category.label} options`}
            >
              {paletteCategory && (
                <div
                  key={paletteCategory}
                  className="avatar-creator__palette"
                  role="group"


                  aria-label={
                    paletteCategory === 'eyeColor'
                      ? 'Choose eye color'
                      : paletteCategory === 'hairColor'
                        ? 'Choose hair color'
                        : 'Choose clothing color'
                  }

                >
                  {paletteOptions.map((option) => {
                    const selected =
                      avatar[paletteCategory] === option.id;

                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={`avatar-creator__color-option ${
                          selected ? 'is-selected' : ''
                        }`}
                        aria-label={option.label}
                        aria-pressed={selected}
                        onClick={() =>
                          selectOption(
                            option.id,
                            paletteCategory
                          )
                        }
                      >
                        <span
                          className="avatar-creator__swatch"
                          style={{
                            backgroundColor: option.color,
                          }}
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>
              )}

              <div
                className={`avatar-creator__options ${
                  isColor ? 'is-colors' : ''
                }`}
                role="group"
                aria-label={`Choose ${category.label.toLowerCase()}`}
              >
                {options.map((option) => {
                  const selected =
                    avatar[category.id] === option.id;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={`avatar-creator__option ${
                        selected ? 'is-selected' : ''
                      }`}
                      aria-label={option.label}
                      aria-pressed={selected}
                      title={option.label}
                      onClick={() => selectOption(option.id)}
                    >
                      {isColor ? (
                        <span
                          className="avatar-creator__swatch"
                          style={{
                            backgroundColor: option.color,
                          }}
                          aria-hidden="true"
                        />
                      ) : (
                        <span
                          className="avatar-creator__miniature"
                          aria-hidden="true"
                        >


                          <AvatarRenderer
                            avatar={{
                              ...avatar,
                              [category.id]: option.id,
                              ...(category.id === 'eyeShape'
                                ? { expression: 'happy_01' }
                                : {}),
                            }}
                            size={200}

                            viewBox={
                              THUMBNAIL_VIEWS[category.id] ||
                              '0 0 400 400'
                            }
                            showBackground={false}
                            rounded={false}
                          />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
